import { useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Avatar, Box, Button, Popover, Skeleton, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType, ValuationNoteChangedPayload } from 'context/EventBusContext/EventBusContext.type';
import { sanitizeNoteHtml } from 'dashboard409a/components/ValuationsTable/common/AnalystNoteHtml.utilities';
import { useNoteHandler } from 'dashboard409a/components/ValuationsTable/common/useNoteHandler';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import RichTextNoteEditor from '../RichTextNoteEditor';
import {
  DeleteNoteButton,
  NOTE_CONTENT_SX,
  NoteAuthorBlock,
  NoteCard,
  NoteHeader,
  NotesList,
} from './AnalystNotesHistoryPanel.styles';
import {
  AnalystNoteHistoryItem,
  AnalystNoteReferenceType,
  AnalystNotesHistoryPanelProps,
  WorkboardTaskOccurrenceReference,
} from './AnalystNotesHistoryPanel.types';
import { getInitials } from './AnalystNotesHistoryPanel.utilities';
import { analystNotesHistoryQueryKey, useAnalystNotesHistoryQuery } from './useAnalystNotesHistoryQuery';

const buildCreateRequest = (
  referenceType: AnalystNoteReferenceType,
  referenceId: string | number | undefined,
  content: string,
  workboardTaskOccurrence?: WorkboardTaskOccurrenceReference
): CreateNoteRequest => {
  if (referenceType === 'at') {
    return {
      content,
      reference_type: 'at',
      attio_valuation_id: referenceId as string,
    };
  }

  if (referenceType === 'fu') {
    return {
      content,
      reference_type: 'fu',
      fulfillment: referenceId as number,
    };
  }

  if (!workboardTaskOccurrence) {
    throw new Error('workboardTaskOccurrence is required for workboard task notes');
  }

  return {
    content,
    reference_type: 'wo',
    workboard_task_id: workboardTaskOccurrence.workboardTaskId,
    occurrence_date: workboardTaskOccurrence.occurrenceDate,
  };
};

const AnalystNotesHistoryPanel = ({
  referenceType,
  referenceId,
  workboardTaskOccurrence,
  isDisabled,
}: AnalystNotesHistoryPanelProps) => {
  const queryClient = useQueryClient();
  const notesService = useMemo(() => new AttioNotesService(), []);
  const { publish } = useEventBus();

  const historyQueryKey = useMemo(
    () => analystNotesHistoryQueryKey(referenceType, referenceId, workboardTaskOccurrence),
    [referenceType, referenceId, workboardTaskOccurrence]
  );

  const [open, setOpen] = useState(false);
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, noteId: number) => {
    setAnchorEl(event.currentTarget);
    setPendingDeleteNoteId(noteId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setPendingDeleteNoteId(null);
  };

  const { data, isFetching, isError } = useAnalystNotesHistoryQuery(
    referenceType,
    referenceId,
    workboardTaskOccurrence
  );

  const notes = data ?? [];

  const [draft, setDraft] = useState('');

  const handleNoteDelete = async (noteId: number) => {
    await notesService.deleteNote(noteId);
    const updatedNotes = notes.filter(n => n.id !== noteId);
    queryClient.setQueryData(historyQueryKey, updatedNotes);
    if (referenceType === 'at' && referenceId) {
      const latestNote = updatedNotes[0] ?? null;
      publish(EventType.VALUATION_NOTE_CHANGED, {
        valuationId: referenceId as string,
        latestNote: latestNote ? { id: latestNote.id, content: latestNote.content } : null,
      } satisfies ValuationNoteChangedPayload);
    }
    handleClose();
  };

  const { isNoteLoading, handleNoteSave } = useNoteHandler({
    onSuccess: async (_id, _content, createdNote) => {
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: historyQueryKey });
      if (referenceType === 'at' && referenceId && createdNote) {
        publish(EventType.VALUATION_NOTE_CHANGED, {
          valuationId: referenceId as string,
          latestNote: { id: createdNote.id ?? 0, content: createdNote.content ?? '' },
        } satisfies ValuationNoteChangedPayload);
      }
    },
  });

  const canCreate =
    !isDisabled &&
    !isNoteLoading &&
    draft.trim().length > 0 &&
    (referenceType === 'wo'
      ? Boolean(workboardTaskOccurrence?.workboardTaskId && workboardTaskOccurrence.occurrenceDate)
      : Boolean(referenceId));

  const initialsSourceFor = (note: AnalystNoteHistoryItem) => note.author_name || note.author_email || '';

  const handleSaveDraft = async () => {
    if (!canCreate) {
      return;
    }

    const request = buildCreateRequest(referenceType, referenceId, draft.trim(), workboardTaskOccurrence);
    const saveReferenceId =
      referenceType === 'wo'
        ? `${workboardTaskOccurrence!.workboardTaskId}:${workboardTaskOccurrence!.occurrenceDate}`
        : referenceId!;
    await handleNoteSave(saveReferenceId, draft.trim(), undefined, request, false);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <NotesList>
        {isFetching && (
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="rounded" width="100%" height={72} />
            <Skeleton variant="rounded" width="100%" height={72} sx={{ mt: 1 }} />
          </Box>
        )}

        {!isFetching && isError && (
          <Typography variant="body2" color="error">
            Failed to load notes.
          </Typography>
        )}

        {!isFetching && !isError && notes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No notes yet.
          </Typography>
        )}

        {!isFetching &&
          !isError &&
          notes.map(note => {
            const createdAtLabel = note.created_at
              ? moment(note.created_at).format('MMM D, YYYY h:mm A')
              : '';

            return (
              <NoteCard key={note.id}>
                <NoteHeader>
                  <NoteAuthorBlock>
                    <Avatar
                      src={note.author_picture_url ?? undefined}
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: '0.7rem',
                        bgcolor: 'primary.main',
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(initialsSourceFor(note))}
                    </Avatar>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {note.author_name || note.author_email}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0, ml: 'auto' }}
                    >
                      {createdAtLabel}
                    </Typography>
                  </NoteAuthorBlock>
                  <DeleteNoteButton
                    size="small"
                    aria-label="Delete note"
                    id={`analyst-note-delete-${note.id}`}
                    onClick={e => handleClick(e, note.id)}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </DeleteNoteButton>
                  <Popover
                    open={open && pendingDeleteNoteId === note.id}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
                      <Typography variant="body2">Are you sure you want to delete this note?</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="small" onClick={() => handleNoteDelete(note.id)}>Delete</Button>
                        <Button size="small" onClick={handleClose}>Cancel</Button>
                      </Box>
                    </Box>
                  </Popover>
                </NoteHeader>

                <Box
                  sx={{ ...NOTE_CONTENT_SX, mt: 0 }}
                  dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.content || '') }}
                />
              </NoteCard>
            );
          })}
      </NotesList>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <RichTextNoteEditor
          content={draft}
          onChange={setDraft}
          isDisabled={isDisabled}
          placeholder="Add note..."
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            id="analyst-notes-save"
            disabled={!canCreate}
            onClick={async () => {
              await handleSaveDraft();
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalystNotesHistoryPanel;
